// c:\Users\cornn\source\repos\purehealthnexus\src\components\nutrition\vitamins\aiUserVmSummary.ts
// Builder for Vitamins & Minerals cards from NutritionTargetsCalculationsSnapshot.
//
// This module follows the same pattern as AiUserMacros.jsx but for vitamins and minerals.
// It transforms the Nutrition Calculate JSON into card models for the AI User Input UI.

// ---------- TypeScript interfaces ----------

export interface VitaminMineralCard {
  id: string;              // stable ID for React keys (e.g., "vitaminD", "magnesium")
  label: string;           // display name (e.g., "Vit D", "Mg", "Iron")
  nutrientKey: string;     // exact key in comparisonVsTargets
  category: 'vitamin' | 'mineral';
  target: number | null;
  actual: number | null;
  percentOfTarget: number | null;  // 0-200 range (for bar width and sorting)
  displayPercent: string | null;    // for UI display (e.g., "98%", "<1%", "–")
  status: 'alert' | 'warn' | 'good' | 'over' | 'no_target';
}

export interface VmHeadlineChip {
  text: string;
  isVisible: boolean;
}

interface NutrientDefinition {
  id: string;
  label: string;
  nutrientKey: string;
  category: 'vitamin' | 'mineral';
}

// ---------- Nutrient Definitions ----------

const VITAMIN_DEFINITIONS: NutrientDefinition[] = [
  { id: 'vitaminA', label: 'Vit A', nutrientKey: 'vitaminA_µg_RE', category: 'vitamin' },
  { id: 'vitaminD', label: 'Vit D', nutrientKey: 'vitaminD_µg', category: 'vitamin' },
  { id: 'vitaminE', label: 'Vit E', nutrientKey: 'vitaminE_mg_aTE', category: 'vitamin' },
  { id: 'vitaminK', label: 'Vit K', nutrientKey: 'vitaminK_µg', category: 'vitamin' },
  { id: 'vitaminC', label: 'Vit C', nutrientKey: 'vitaminC_mg', category: 'vitamin' },
  { id: 'thiamin', label: 'B1', nutrientKey: 'thiamin_mg', category: 'vitamin' },
  { id: 'riboflavin', label: 'B2', nutrientKey: 'riboflavin_mg', category: 'vitamin' },
  { id: 'niacin', label: 'B3', nutrientKey: 'niacin_NE', category: 'vitamin' },
  { id: 'pantothenicAcid', label: 'B5', nutrientKey: 'pantothenicAcid_mg', category: 'vitamin' },
  { id: 'vitaminB6', label: 'B6', nutrientKey: 'vitaminB6_mg', category: 'vitamin' },
  { id: 'biotin', label: 'B7', nutrientKey: 'biotin_µg', category: 'vitamin' },
  { id: 'folate', label: 'B9', nutrientKey: 'folate_µg', category: 'vitamin' },
  { id: 'vitaminB12', label: 'B12', nutrientKey: 'vitaminB12_µg', category: 'vitamin' },
  { id: 'choline', label: 'Choline', nutrientKey: 'choline_mg', category: 'vitamin' },
];

const MINERAL_DEFINITIONS: NutrientDefinition[] = [
  { id: 'calcium', label: 'Calcium', nutrientKey: 'calcium_mg', category: 'mineral' },
  { id: 'magnesium', label: 'Mg', nutrientKey: 'magnesium_mg', category: 'mineral' },
  { id: 'phosphorus', label: 'P', nutrientKey: 'phosphorus_mg', category: 'mineral' },
  { id: 'potassium', label: 'K', nutrientKey: 'potassium_mg', category: 'mineral' },
  { id: 'sodium', label: 'Sodium', nutrientKey: 'salt_g', category: 'mineral' },
  { id: 'iron', label: 'Iron', nutrientKey: 'iron_mg', category: 'mineral' },
  { id: 'zinc', label: 'Zinc', nutrientKey: 'zinc_mg', category: 'mineral' },
  { id: 'copper', label: 'Cu', nutrientKey: 'copper_µg', category: 'mineral' },
  { id: 'selenium', label: 'Se', nutrientKey: 'selenium_µg', category: 'mineral' },
  { id: 'manganese', label: 'Mn', nutrientKey: 'manganese_mg', category: 'mineral' },
  { id: 'fluoride', label: 'F', nutrientKey: 'fluoride_mg', category: 'mineral' },
  { id: 'molybdenum', label: 'Mo', nutrientKey: 'molybdenum_µg', category: 'mineral' },
  { id: 'iodine', label: 'I', nutrientKey: 'iodine_µg', category: 'mineral' },
];

// ---------- Helper Functions ----------

/**
 * Compute status from percent of target.
 * - no_target: no target available or invalid percent
 * - alert: < 70%
 * - warn: 70% - 79%
 * - good: 80% - 120%
 * - over: > 120%
 */
function computeStatus(
  percentOfTarget: number | null,
  hasTarget: boolean
): VitaminMineralCard['status'] {
  if (!hasTarget || percentOfTarget == null) {
    return 'no_target';
  }

  if (percentOfTarget < 70) return 'alert';
  if (percentOfTarget < 80) return 'warn';
  if (percentOfTarget <= 120) return 'good';
  return 'over';
}

/**
 * Build a single vitamin or mineral card from the nutrient definition
 * and the comparison data from the snapshot.
 */
function buildCard(
  definition: NutrientDefinition,
  comparisonVsTargets: any
): VitaminMineralCard {
  const comparison = comparisonVsTargets?.[definition.nutrientKey];

  let target: number | null = null;
  let actual: number | null = null;
  let percentOfTarget: number | null = null;
  let displayPercent: string | null = null;

  if (comparison && typeof comparison === 'object') {
    target = typeof comparison.target === 'number' ? comparison.target : null;
    actual = typeof comparison.actual === 'number' ? comparison.actual : null;

    // Always compute raw percentage from actual and target for accurate trace detection
    let rawPercent: number | null = null;
    if (target != null && target > 0 && actual != null) {
      rawPercent = (actual / target) * 100;
    }

    if (rawPercent != null) {
      // For display: show "<1%" when there's intake but it's less than 1%
      if (rawPercent > 0 && rawPercent < 1) {
        displayPercent = '<1%';
        percentOfTarget = 1; // use 1 for bar width and sorting
      } else {
        percentOfTarget = Math.round(rawPercent);
        displayPercent = `${percentOfTarget}%`;
      }

      // Clamp percentOfTarget to [0, 200] for bar width
      percentOfTarget = Math.min(200, Math.max(0, percentOfTarget));
    }
  }

  const hasTarget = target != null && target > 0;
  const status = computeStatus(percentOfTarget, hasTarget);

  return {
    id: definition.id,
    label: definition.label,
    nutrientKey: definition.nutrientKey,
    category: definition.category,
    target,
    actual,
    percentOfTarget,
    displayPercent,
    status,
  };
}

// ---------- Public Builders ----------

/**
 * Build vitamin cards from a Nutrition Calculate snapshot.
 * Returns all 14 vitamins with their current status.
 */
export function buildAiUserVitaminCardsFromNutritionCalculateSnapshot(
  snapshot: any
): VitaminMineralCard[] {
  if (!snapshot || typeof snapshot !== 'object') {
    // Return neutral/empty cards when no snapshot available
    return VITAMIN_DEFINITIONS.map((def) => ({
      id: def.id,
      label: def.label,
      nutrientKey: def.nutrientKey,
      category: def.category,
      target: null,
      actual: null,
      percentOfTarget: null,
      displayPercent: null,
      status: 'no_target' as const,
    }));
  }

  const comparisonVsTargets = snapshot.intakeSummary?.comparisonVsTargets || {};

  return VITAMIN_DEFINITIONS.map((def) =>
    buildCard(def, comparisonVsTargets)
  );
}

/**
 * Build mineral cards from a Nutrition Calculate snapshot.
 * Returns all 13 minerals with their current status.
 */
export function buildAiUserMineralCardsFromNutritionCalculateSnapshot(
  snapshot: any
): VitaminMineralCard[] {
  if (!snapshot || typeof snapshot !== 'object') {
    // Return neutral/empty cards when no snapshot available
    return MINERAL_DEFINITIONS.map((def) => ({
      id: def.id,
      label: def.label,
      nutrientKey: def.nutrientKey,
      category: def.category,
      target: null,
      actual: null,
      percentOfTarget: null,
      displayPercent: null,
      status: 'no_target' as const,
    }));
  }

  const comparisonVsTargets = snapshot.intakeSummary?.comparisonVsTargets || {};

  return MINERAL_DEFINITIONS.map((def) =>
    buildCard(def, comparisonVsTargets)
  );
}

/**
 * Build headline chip text showing the two lowest nutrients.
 * The chip should be visible when the section is collapsed.
 */
export function buildAiUserVmHeadlineChip(
  vitaminCards: VitaminMineralCard[],
  mineralCards: VitaminMineralCard[]
): VmHeadlineChip {
  // Combine all cards
  const allCards = [...vitaminCards, ...mineralCards];

  // Filter to cards with valid percentOfTarget
  const cardsWithPercent = allCards.filter(
    (card) => card.percentOfTarget != null && card.status !== 'no_target'
  );

  if (cardsWithPercent.length === 0) {
    return {
      text: '',
      isVisible: false,
    };
  }

  // Sort by percentOfTarget ascending (lowest first)
  const sorted = [...cardsWithPercent].sort(
    (a, b) => (a.percentOfTarget || 0) - (b.percentOfTarget || 0)
  );

  // Take the two lowest
  const lowest = sorted.slice(0, 2);

  const text = lowest
    .map((card) => `${card.label} ${card.displayPercent || '–'}`)
    .join(' • ');

  return {
    text,
    isVisible: true,
  };
}
